import apiService from './apiService';

// TypeScript interfaces
export interface PaymentAllocation {
  id?: string;
  paymentId?: string;
  invoiceId?: string;
  billId?: string;
  amountAllocated: number;
  createdAt?: string;
}

export interface Payment {
  id: string;
  paymentNo: string;
  paymentType: 'INBOUND' | 'OUTBOUND';
  partnerType: 'CUSTOMER' | 'VENDOR';
  partnerId: string;
  amount: number;
  paymentDate: string;
  paymentMethodId: string;
  journalId: string;
  accountId?: string; // Chart of Account - which cash/bank account this payment goes into
  memo?: string;
  state: 'DRAFT' | 'POSTED' | 'CANCELLED';
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  invoiceAllocations?: PaymentAllocation[];
  billAllocations?: PaymentAllocation[];
  partner?: {
    id: string;
    name: string;
    email?: string;
  };
  paymentMethod?: {
    id: string;
    name: string;
  };
  journal?: {
    id: string;
    name: string;
    code: string;
  };
  account?: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
}

export interface CreatePaymentInput {
  paymentType: 'INBOUND' | 'OUTBOUND';
  partnerType: 'CUSTOMER' | 'VENDOR';
  partnerId: string;
  amount: number;
  paymentDate: string;
  paymentMethodId: string;
  journalId: string;
  accountId?: string; // Chart of Account - which cash/bank account this payment goes into
  memo?: string;
  state?: 'DRAFT' | 'POSTED';
  allocations?: {
    invoiceId?: string;
    billId?: string;
    amountAllocated: number;
  }[];
}

export interface UpdatePaymentInput {
  amount?: number;
  paymentDate?: string;
  paymentMethodId?: string;
  accountId?: string; // Chart of Account - which cash/bank account this payment goes into
  memo?: string;
  state?: string;
}

export interface PaymentFilters {
  paymentType?: string;
  partnerId?: string;
  state?: string;
  fromDate?: string;
  toDate?: string;
}

export interface PostPaymentInput {
  cashAccountId: string;
  arAccountId: string; // Accounts Receivable
  apAccountId: string; // Accounts Payable
  journalId: string;
}

export interface PaymentStats {
  totalPayments: number;
  inboundPayments: number;
  outboundPayments: number;
  totalInboundAmount: number;
  totalOutboundAmount: number;
  draftPayments: number;
  postedPayments: number;
}

class PaymentService {
  private baseUrl = '/accounting/payments';

  /**
   * Get all payments with optional filters
   */
  async getAllPayments(filters?: PaymentFilters): Promise<Payment[]> {
    const params = new URLSearchParams();
    if (filters?.paymentType) params.append('paymentType', filters.paymentType);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await apiService.get(url);
    return response.data;
  }

  /**
   * Get payment by ID
   */
  async getPaymentById(id: string): Promise<Payment> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get payment by payment number
   */
  async getPaymentByNumber(paymentNo: string): Promise<Payment> {
    const response = await apiService.get(`${this.baseUrl}/number/${paymentNo}`);
    return response.data;
  }

  /**
   * Create a new payment
   */
  async createPayment(data: CreatePaymentInput): Promise<Payment> {
    const response = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing payment
   */
  async updatePayment(id: string, data: UpdatePaymentInput): Promise<Payment> {
    const response = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Post a payment (DRAFT → POSTED)
   * Creates a journal entry automatically
   */
  async postPayment(id: string, data: PostPaymentInput): Promise<Payment> {
    const response = await apiService.post(`${this.baseUrl}/${id}/post`, data);
    return response.data;
  }

  /**
   * Cancel a payment
   * Creates a reversing journal entry if posted
   */
  async cancelPayment(id: string, data: PostPaymentInput): Promise<Payment> {
    const response = await apiService.post(`${this.baseUrl}/${id}/cancel`, data);
    return response.data;
  }

  /**
   * Delete a payment
   */
  async deletePayment(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get payments by partner (customer or vendor)
   */
  async getPaymentsByPartner(partnerId: string): Promise<Payment[]> {
    const response = await apiService.get(`${this.baseUrl}/partner/${partnerId}`);
    return response.data;
  }

  /**
   * Get payment statistics
   */
  async getPaymentStats(): Promise<PaymentStats> {
    const response = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Get unallocated amount for a payment
   */
  async getUnallocatedAmount(paymentId: string): Promise<number> {
    const response = await apiService.get(`${this.baseUrl}/${paymentId}/unallocated`);
    return response.data.unallocatedAmount;
  }

  /**
   * Allocate payment to invoices
   */
  async allocatePayment(
    paymentId: string,
    allocations: { invoiceId?: string; billId?: string; amountAllocated: number }[]
  ): Promise<Payment> {
    const response = await apiService.post(`${this.baseUrl}/${paymentId}/allocate`, {
      allocations
    });
    return response.data;
  }
}

export default new PaymentService();